import React, { useState } from "react";
import { toast } from "sonner";
import { ShoppingCart, Loader2, FileText, FileSpreadsheet, Save, History } from "lucide-react";
import api, { formatMoney, formatMoney2, formatNumber, formatApiErrorDetail } from "../../lib/api";
import { Pregunta } from "./Familias";
import { AnadirProducto, EstadoBadge, QuitarExtra } from "./AnadirProducto";

const DIAS = [15, 30, 60, 90];

function descargarBlob(data, type, nombre) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([data], { type }));
  a.download = nombre; a.click();
}

export function PedidoPropuesto({ lab, seg, familia, inicial, onGuardado }) {
  const [paso, setPaso] = useState(inicial ? 1 : 0);
  const [dias, setDias] = useState(inicial?.dias || 30);
  const [ped, setPed] = useState(inicial || null);
  const [cargando, setCargando] = useState("");
  const [ajustes, setAjustes] = useState({});
  const [guardado, setGuardado] = useState(inicial || null);
  const [extras, setExtras] = useState([]);
  const params = { laboratorio: lab, segment: seg, familia: familia || undefined, dias, pedido_id: guardado?.id };
  const cambiarDias = (d) => { setDias(d); setPed(null); setGuardado(null); setExtras([]); };
  const cuerpo = () => ({ ...params, ajustes, extras: extras.map((e) => ({ cn: e.cn, descripcion: e.descripcion, unidades: e.unidades, coste: e.coste, nuevo: e.estado === "nuevo" })) });

  const guardar = async () => {
    setCargando("guardar");
    try {
      const { data } = await api.post("/lab-analisis/pedidos", cuerpo());
      const full = (await api.get(`/lab-analisis/pedidos/${data.id}`)).data;
      toast.success("Pedido guardado"); setGuardado(full); setPed(full); setExtras([]); setAjustes({}); onGuardado?.(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo guardar el pedido"); }
    finally { setCargando(""); }
  };

  const proponer = async () => {
    setCargando("ver");
    try { setPed((await api.get("/lab-analisis/pedido", { params: { ...params, pedido_id: undefined } })).data); setAjustes({}); setGuardado(null); setExtras([]); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo calcular el pedido"); }
    finally { setCargando(""); }
  };
  const bajar = async (fmt) => {
    setCargando(fmt);
    try {
      const r = await api.post(`/lab-analisis/pedido/${fmt}`, cuerpo(), { responseType: "blob" });
      descargarBlob(r.data, fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        `pedido-${lab}${familia ? `-${familia}` : ""}-${dias}d.${fmt === "pdf" ? "pdf" : "xlsx"}`);
    } catch { toast.error("No se pudo descargar el pedido"); } finally { setCargando(""); }
  };

  if (paso === 0) {
    return <Pregunta icon={ShoppingCart} testid="la-pregunta-comprar" titulo="¿Quieres comprar a este laboratorio?"
                     texto={`Te propongo un pedido de ${familia ? `la familia ${familia} de ` : ""}${lab} según los días que quieras cubrir.`}
                     si="Sí, proponme un pedido" onSi={() => setPaso(1)} />;
  }
  return (
    <div className="card p-6" data-testid="la-pedido">
      <h3 className="font-display text-lg font-bold flex items-center gap-2"><ShoppingCart size={18} className="text-sky-700" /> Pedido propuesto</h3>
      <p className="text-xs text-slate-500 mb-4">Unidades = venta media diaria × días a cubrir − stock actual. Solo aparecen los productos que se quedarían cortos. Puedes cambiar las unidades de cada línea (0 = no pedir) y añadir otros productos antes de descargar.</p>
      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="la-pedido-dias">
        <span className="text-sm font-semibold text-slate-700">¿Cuántos días quieres cubrir?</span>
        {DIAS.map((d) => (
          <button key={d} onClick={() => cambiarDias(d)} data-testid={`la-dias-${d}`}
                  className={"px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors " + (dias === d ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-sky-400")}>{d} días</button>
        ))}
        <input type="number" min={1} max={365} value={dias} onChange={(e) => cambiarDias(Math.max(1, Math.min(365, +e.target.value || 1)))}
               className="input" style={{ width: 96 }} data-testid="la-dias-input" />
        <button onClick={proponer} disabled={cargando === "ver"} className="btn btn-primary" data-testid="la-pedido-calcular">
          {cargando === "ver" ? <Loader2 size={14} className="animate-spin" /> : <ShoppingCart size={14} />} Proponer pedido
        </button>
      </div>
      {guardado && (
        <div className="mb-3 rounded-lg bg-sky-50 border border-sky-200 px-4 py-2 text-sm text-sky-900 flex items-center gap-2" data-testid="la-pedido-guardado-aviso">
          <History size={15} /> Pedido guardado el {new Date(guardado.created_at).toLocaleDateString("es-ES")} · {guardado.dias} días. Puedes retocarlo y descargarlo o guardarlo como uno nuevo.
        </div>
      )}
      {ped && <AnadirProducto lab={lab} dias={dias} existentes={new Set([...ped.lineas, ...extras].map((r) => r.cn))}
                             onAdd={(p) => { setExtras((x) => [...x, p]); toast.success(`Añadido: ${p.descripcion}`); }} />}
      {ped && <PedidoTabla lineas={[...ped.lineas, ...extras]} dias={ped.dias} ajustes={ajustes} setAjustes={setAjustes} cargando={cargando} onBajar={bajar} onGuardar={guardar}
                           esExtra={(cn) => extras.some((e) => e.cn === cn)} onQuitar={(cn) => setExtras((x) => x.filter((e) => e.cn !== cn))} />}
    </div>
  );
}

function PedidoTabla({ lineas, dias, ajustes, setAjustes, cargando, onBajar, onGuardar, esExtra, onQuitar }) {
  const uds = (r) => ajustes[r.cn] ?? r.unidades;
  const totUds = lineas.reduce((t, r) => t + uds(r), 0);
  const totEur = lineas.reduce((t, r) => t + uds(r) * r.coste, 0);
  const lineasActivas = lineas.filter((r) => uds(r) > 0).length;
  const cambiados = lineas.filter((r) => !esExtra(r.cn) && uds(r) !== r.unidades).length;
  if (!lineas.length) return <p className="text-sm text-slate-500 py-4" data-testid="la-pedido-vacio">Con el stock actual tienes cubiertos {dias} días: no hace falta pedir nada. Si quieres, añade productos arriba.</p>;
  return (
    <>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="text-sm" data-testid="la-pedido-total"><b>{formatNumber(lineasActivas)}</b> productos · <b>{formatNumber(totUds)}</b> uds · <b className="text-sky-800">{formatMoney(totEur)}</b> a coste
          {cambiados > 0 && <span className="ml-2 text-xs text-amber-700" data-testid="la-pedido-cambiados">{cambiados} {cambiados === 1 ? "línea ajustada" : "líneas ajustadas"} · <button onClick={() => setAjustes({})} className="underline font-semibold" data-testid="la-pedido-restaurar">volver a lo propuesto</button></span>}</div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={onGuardar} disabled={!!cargando || !lineasActivas} className="btn btn-primary btn-sm" data-testid="la-pedido-guardar">
            {cargando === "guardar" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar pedido</button>
          <button onClick={() => onBajar("pdf")} disabled={!!cargando || !lineasActivas} className="btn btn-outline btn-sm" data-testid="la-pedido-pdf">
            {cargando === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} PDF para el delegado</button>
          <button onClick={() => onBajar("excel")} disabled={!!cargando || !lineasActivas} className="btn btn-outline btn-sm" data-testid="la-pedido-excel">
            {cargando === "excel" ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />} Excel</button>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
        <table className="w-full text-sm" data-testid="la-pedido-tabla">
          <thead className="sticky top-0 bg-white"><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b">
            <th className="py-2 pr-3">Producto</th><th>Clase</th><th className="num">Stock</th><th className="num">Uds vend.</th><th className="num">Últ. venta</th><th className="num">Últ. entrada</th><th className="num">Venta/día</th>
            <th className="num">Pedir</th><th className="num">Coste ud.</th><th className="num">Importe</th></tr></thead>
          <tbody>
            {lineas.map((r) => (
              <tr key={r.cn} className={"border-b border-slate-100 " + (r.manual ? "bg-sky-50/40 " : "") + (uds(r) === 0 ? "opacity-40" : "")} data-testid={`la-pedido-linea-${r.cn}`}>
                <td className="py-2 pr-3"><div className="font-semibold flex items-center gap-1">{r.descripcion}{esExtra(r.cn) && <QuitarExtra cn={r.cn} onClick={() => onQuitar(r.cn)} />}</div>
                  <div className="text-[11px] text-slate-400 flex items-center gap-2 flex-wrap">CN {r.cn}{r.familia ? ` · ${r.familia}` : ""}{r.manual && <><span className="font-semibold text-sky-700">Añadido</span><EstadoBadge r={r} /></>}</div></td>
                <td className="font-bold">{r.abcd || (r.estado === "nuevo" ? <span className="text-sky-700 text-xs">NUEVO</span> : "—")}</td><td className="num">{formatNumber(r.stock)}</td>
                <td className="num">{r.uds_vendidas != null ? formatNumber(r.uds_vendidas) : "—"}</td>
                <td className="num whitespace-nowrap">{r.u_salida ? new Date(r.u_salida).toLocaleDateString("es-ES") : "—"}</td>
                <td className="num whitespace-nowrap">{r.u_entrada ? new Date(r.u_entrada).toLocaleDateString("es-ES") : "—"}</td>
                <td className="num">{String(r.venta_diaria).replace(".", ",")}</td>
                <td className="num">
                  <input type="number" min={0} value={uds(r)} data-testid={`la-pedido-uds-${r.cn}`}
                         onChange={(e) => setAjustes((a) => ({ ...a, [r.cn]: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                         className={"w-16 text-right font-extrabold rounded-md border px-2 py-1 " + (uds(r) !== r.unidades ? "border-amber-400 bg-amber-50 text-amber-800" : "border-slate-200 text-sky-800")} />
                </td>
                <td className="num">{formatMoney2(r.coste)}</td><td className="num">{formatMoney(uds(r) * r.coste)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
